import { supabase } from "@/integrations/supabase/client";

export type TenantBranding = {
  tenant_id: string;
  tenant_slug: string;
  brand_name: string;
  brand_tagline: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  hero_image_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  font_family_heading: string | null;
  font_family_body: string | null;
  border_radius: string | null;
  show_powered_by_zyntra: boolean;
};

/**
 * Branding fetcher — runs on the client using the anon key.
 * RLS on tenants/tenant_branding allows public SELECT for active tenants,
 * so no service role is required.
 */
export async function getBrandingForEvent(eventId: string): Promise<TenantBranding | null> {
  try {
    const { data, error } = await supabase.rpc("get_branding_for_event", { _event_id: eventId });
    if (!error && data && data.length > 0) {
      return data[0] as TenantBranding;
    }
  } catch (e) {
    console.error("[branding] rpc failed", e);
  }
  return getDefaultBranding();
}

/**
 * Resolve branding for a list of event IDs in a single round-trip.
 * Returns a Map<eventId, TenantBranding> with fallback to default for any
 * event whose tenant cannot be resolved. Used by /my-tickets to avoid N+1.
 */
export async function getBrandingForEvents(
  eventIds: string[]
): Promise<Map<string, TenantBranding>> {
  const result = new Map<string, TenantBranding>();
  const unique = Array.from(new Set(eventIds.filter(Boolean)));
  if (unique.length === 0) return result;

  const fallback = await getDefaultBranding();

  // Pull tenant_id for each event in one query, then branding per tenant.
  const { data: events } = await supabase
    .from("events")
    .select("id, tenant_id")
    .in("id", unique);

  const tenantIds = Array.from(
    new Set((events ?? []).map((e) => e.tenant_id).filter((v): v is string => !!v))
  );

  let brandingByTenant = new Map<string, TenantBranding>();
  if (tenantIds.length > 0) {
    const { data: tenants } = await supabase
      .from("tenants")
      .select("id, slug, status")
      .in("id", tenantIds)
      .eq("status", "active");
    const activeIds = (tenants ?? []).map((t) => t.id);
    const slugById = new Map((tenants ?? []).map((t) => [t.id, t.slug]));

    if (activeIds.length > 0) {
      const { data: brandingRows } = await supabase
        .from("tenant_branding")
        .select("*")
        .in("tenant_id", activeIds);
      for (const b of brandingRows ?? []) {
        brandingByTenant.set(b.tenant_id, {
          tenant_id: b.tenant_id,
          tenant_slug: slugById.get(b.tenant_id) ?? "",
          brand_name: b.brand_name,
          brand_tagline: b.brand_tagline,
          logo_url: b.logo_url,
          favicon_url: b.favicon_url,
          hero_image_url: b.hero_image_url,
          primary_color: b.primary_color,
          secondary_color: b.secondary_color,
          accent_color: b.accent_color,
          font_family_heading: b.font_family_heading,
          font_family_body: b.font_family_body,
          border_radius: b.border_radius,
          show_powered_by_zyntra: b.show_powered_by_zyntra,
        });
      }
    }
  }

  const eventTenantMap = new Map((events ?? []).map((e) => [e.id, e.tenant_id]));
  for (const eventId of unique) {
    const tenantId = eventTenantMap.get(eventId);
    const tenantBranding = tenantId ? brandingByTenant.get(tenantId) : undefined;
    if (tenantBranding) {
      result.set(eventId, tenantBranding);
    } else if (fallback) {
      result.set(eventId, fallback);
    }
  }
  return result;
}

export async function getDefaultBranding(): Promise<TenantBranding | null> {
  const { data: tenant } = await supabase
    .from("tenants")
    .select("id, slug")
    .eq("is_default", true)
    .eq("status", "active")
    .maybeSingle();
  if (!tenant) return null;
  const { data: b } = await supabase
    .from("tenant_branding")
    .select("*")
    .eq("tenant_id", tenant.id)
    .maybeSingle();
  if (!b) return null;
  return {
    tenant_id: tenant.id,
    tenant_slug: tenant.slug,
    brand_name: b.brand_name,
    brand_tagline: b.brand_tagline,
    logo_url: b.logo_url,
    favicon_url: b.favicon_url,
    hero_image_url: b.hero_image_url,
    primary_color: b.primary_color,
    secondary_color: b.secondary_color,
    accent_color: b.accent_color,
    font_family_heading: b.font_family_heading,
    font_family_body: b.font_family_body,
    border_radius: b.border_radius,
    show_powered_by_zyntra: b.show_powered_by_zyntra,
  };
}
